import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import planService, {
  CATEGORIA_DO_CADASTRO,
  Plan,
  planosDoCadastro,
} from "@/services/plan.service";
import { useAuth } from "@/contexts/auth.context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Sparkles,
  Crown,
  Zap,
  Shield,
  ChevronRight,
  Info,
  Gift,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CheckoutInitialData {
  name?: string;
  email?: string;
  [key: string]: unknown;
}

const FeatureItem = ({
  text,
  enabled = true,
  highlight = false,
}: {
  text: string;
  enabled?: boolean;
  highlight?: boolean;
}) => (
  <li
    className={cn(
      "flex items-center gap-3 py-1.5",
      !enabled && "text-slate-400 line-through",
    )}
  >
    {enabled ? (
      <CheckCircle2
        className={cn(
          "h-4 w-4 flex-shrink-0",
          highlight ? "text-amber-500" : "text-[#9CCB3B]",
        )}
      />
    ) : (
      <XCircle className="h-4 w-4 flex-shrink-0 text-slate-300" />
    )}
    <span
      className={cn(
        "text-sm",
        highlight && enabled
          ? "font-medium text-amber-600"
          : enabled
            ? "text-slate-600"
            : "",
      )}
    >
      {text}
    </span>
  </li>
);

export default function PlanSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const initialData = (location.state ?? {}) as CheckoutInitialData;
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const planCategory = CATEGORIA_DO_CADASTRO;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await planService.listPublicPlans();
        setPlans(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sortedPlans = useMemo(() => planosDoCadastro(plans), [plans]);

  const userName = user?.name || initialData.name || "";

  const onChoose = (plan: Plan) => {
    navigate("/register/checkout", {
      state: {
        ...initialData,
        name: userName,
        email: user?.email || initialData.email,
        plan,
        billingCycle,
        planCategory,
      },
    });
  };

  const getFirstName = (fullName: string) => {
    if (!fullName) return null;
    return fullName.split(" ")[0];
  };

  const getPlanIcon = (planType: string) => {
    const iconMap: Record<string, LucideIcon> = {
      free: Shield,
      basic: Shield,
      pro: Zap,
      enterprise: Crown,
    };
    const Icon = iconMap[planType] || Shield;
    return Icon;
  };

  const isPopular = (plan: Plan) => {
    return plan.type === "pro";
  };

  const getYearlyDiscount = (plan: Plan) => {
    const monthlyTotal = plan.price_monthly * 12;
    const yearlyTotal = plan.price_yearly;
    if (yearlyTotal >= monthlyTotal) return 0;
    return Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-[#2869C1] mx-auto" />
          </div>
          <p className="text-slate-500 text-sm">Carregando planos...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Logado, a pessoa chegou aqui pelo painel (a aba Assinatura, ou o desvio de
                  `ExigePlanoAtivo`), e o cadastro não é para onde voltar: `/register` lhe
                  ofereceria criar outra conta. Minha Conta abre sem plano. */}
              <Button
                variant="ghost"
                onClick={() =>
                  navigate(user ? "/dashboard/my-account" : "/register")
                }
                className="text-slate-300 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div className="flex items-center gap-2">
                <img
                  src="/logoSidebar.png"
                  alt="Painel do E-Commerce"
                  className="h-8"
                />
              </div>
              {getFirstName(userName) && (
                <div className="text-sm text-slate-300">
                  Olá,{" "}
                  <span className="font-medium text-white">
                    {getFirstName(userName)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Escolha o plano ideal
            </h1>
            <p className="text-lg text-slate-600">
              Transforme seus dados em resultados. Comece grátis e evolua quando
              precisar.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-left">
              <Gift className="h-6 w-6 flex-shrink-0 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold text-emerald-700">
                  7 dias grátis em qualquer plano pago
                </p>
                <p className="text-xs text-emerald-600">
                  Use todos os recursos do plano e só pague depois do período de
                  teste.
                </p>
              </div>
            </div>
          </div>

          {/* Billing Cycle Selector */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center p-1 bg-white border border-slate-200 rounded-full shadow-sm">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={cn(
                  "px-6 py-2.5 rounded-full text-sm font-medium transition-all",
                  billingCycle === "monthly"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900",
                )}
              >
                Mensal
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={cn(
                  "px-6 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2",
                  billingCycle === "yearly"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900",
                )}
              >
                Anual
                <span className="text-xs bg-[#9CCB3B] text-white px-2 py-0.5 rounded-full">
                  Economize até 20%
                </span>
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {sortedPlans.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <Shield className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">
                  Nenhum plano disponível no momento.
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Entre em contato conosco para mais informações.
                </p>
              </div>
            ) : (
              sortedPlans.map((plan) => {
                const popular = isPopular(plan);
                const Icon = getPlanIcon(plan.type);
                const discount = getYearlyDiscount(plan);
                const price =
                  billingCycle === "monthly"
                    ? plan.price_monthly
                    : plan.price_yearly / 12;
                const isFree = plan.price_monthly === 0;

                return (
                  <div
                    key={plan.id}
                    className={cn(
                      "relative bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-xl flex flex-col",
                      popular
                        ? "border-[#9CCB3B] shadow-lg scale-[1.02] lg:-mt-4 lg:mb-4"
                        : "border-slate-200 hover:border-slate-300",
                    )}
                  >
                    {/* Popular Badge */}
                    {popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-[#9CCB3B] hover:bg-[#9CCB3B] text-white px-4 py-1 text-xs font-semibold shadow-lg">
                          <Sparkles className="h-3 w-3 mr-1" />
                          MAIS POPULAR
                        </Badge>
                      </div>
                    )}

                    {/* Header */}
                    <div
                      className={cn("p-6 pb-4 text-center", popular && "pt-8")}
                    >
                      <div
                        className={cn(
                          "w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center",
                          popular ? "bg-[#9CCB3B]/10" : "bg-slate-100",
                        )}
                      >
                        <Icon
                          className={cn(
                            "h-6 w-6",
                            popular ? "text-[#9CCB3B]" : "text-slate-500",
                          )}
                        />
                      </div>

                      <h3 className="text-xl font-bold text-slate-900 mb-1">
                        {plan.name}
                      </h3>

                      <p className="text-sm text-slate-500 min-h-[40px]">
                        {plan.description ||
                          `Plano ${plan.name} com funcionalidades completas`}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="px-6 py-4 text-center border-t border-slate-100">
                      {isFree ? (
                        <div className="text-4xl font-bold text-slate-900">
                          Grátis
                        </div>
                      ) : (
                        <>
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-sm text-slate-500">R$</span>
                            <span className="text-4xl font-bold text-slate-900">
                              {price.toFixed(2).replace(".", ",")}
                            </span>
                            <span className="text-slate-500">/mês</span>
                          </div>

                          {billingCycle === "yearly" && discount > 0 && (
                            <div className="mt-2 flex items-center justify-center gap-2">
                              <span className="text-sm text-slate-400 line-through">
                                R${" "}
                                {plan.price_monthly
                                  .toFixed(2)
                                  .replace(".", ",")}
                              </span>
                              <Badge
                                variant="secondary"
                                className="bg-emerald-100 text-emerald-700 text-xs"
                              >
                                -{discount}%
                              </Badge>
                            </div>
                          )}

                          {billingCycle === "yearly" && (
                            <p className="text-xs text-slate-400 mt-1">
                              Cobrado anualmente: R${" "}
                              {plan.price_yearly.toFixed(2).replace(".", ",")}
                            </p>
                          )}
                        </>
                      )}

                      {plan.trial_days > 0 && !isFree && (
                        <Badge
                          variant="outline"
                          className="mt-3 text-emerald-600 border-emerald-200 bg-emerald-50"
                        >
                          {plan.trial_days} dias grátis
                        </Badge>
                      )}
                    </div>

                    {/* Features */}
                    <div className="px-6 py-4 flex-1 border-t border-slate-100">
                      {plan.plan_features && plan.plan_features.length > 0 ? (
                        <ul className="space-y-1">
                          {plan.plan_features.slice(0, 8).map((pf) => (
                            <FeatureItem
                              key={pf.id}
                              text={pf.name}
                              enabled={!!pf.is_enabled}
                              highlight={!!pf.is_highlighted && !!pf.is_enabled}
                            />
                          ))}
                          {plan.plan_features.length > 8 && (
                            <li className="pt-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="text-xs text-[#2869C1] hover:underline flex items-center gap-1">
                                    <Info className="h-3 w-3" />+
                                    {plan.plan_features.length - 8} recursos
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="max-w-xs"
                                >
                                  <ul className="text-xs space-y-1">
                                    {plan.plan_features.slice(8).map((pf) => (
                                      <li
                                        key={pf.id}
                                        className={
                                          !pf.is_enabled ? "text-slate-400" : ""
                                        }
                                      >
                                        {pf.is_enabled ? "✓" : "✗"} {pf.name}
                                      </li>
                                    ))}
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </li>
                          )}
                        </ul>
                      ) : (
                        <div className="text-center py-4 text-slate-400">
                          <p className="text-sm">
                            Funcionalidades em preparação
                          </p>
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="p-6 pt-4">
                      <Button
                        className={cn(
                          "w-full py-6 text-base font-semibold transition-all",
                          popular
                            ? "bg-[#9CCB3B] hover:bg-[#8bba34] shadow-lg hover:shadow-xl"
                            : "bg-[#2869C1] hover:bg-[#1d5299]",
                        )}
                        onClick={() => onChoose(plan)}
                      >
                        {isFree
                          ? "Começar grátis"
                          : popular
                            ? "Começar agora"
                            : "Escolher plano"}
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Trust Badges */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-8 px-8 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Shield className="h-5 w-5 text-[#9CCB3B]" />
                <span className="text-sm font-medium">Pagamento Seguro</span>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="h-5 w-5 text-[#9CCB3B]" />
                <span className="text-sm font-medium">
                  Cancele quando quiser
                </span>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-2 text-slate-600">
                <Sparkles className="h-5 w-5 text-[#9CCB3B]" />
                <span className="text-sm font-medium">7 dias de garantia</span>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-8 text-center text-sm text-slate-500">
            <p>
              Todos os planos incluem garantia de satisfação. Cancele com
              reembolso em até 7 dias após a ativação.
            </p>
            <p className="mt-2">
              Ao continuar, você concorda com os{" "}
              <a
                href="https://www.paineldoecommerce.com.br/termos-de-uso/"
                target="_blank"
                rel="noreferrer"
                className="text-[#2869C1] hover:underline"
              >
                Termos de Uso
              </a>{" "}
              e{" "}
              <a
                href="https://www.paineldoecommerce.com.br/politica-de-privacidade/"
                target="_blank"
                rel="noreferrer"
                className="text-[#2869C1] hover:underline"
              >
                Política de Privacidade
              </a>
              .
            </p>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
